// The one full-screen pass: print grade (tone curve, palette pull, grain, vignette,
// misregistration) for the world, then the visor HUD composited on top with barrel
// curvature and chromatic split. Budget < 0.5 ms at 1080p.

import * as THREE from 'three';
import { HAZE } from './materials';

const VERT = /* glsl */ `
varying vec2 vUv;
void main() { vUv = uv; gl_Position = vec4( position.xy, 0.0, 1.0 ); }
`;

// bright pass: a 4-tap box down to quarter res, keeping only what overflows the white point.
// The lit world tops out near 1.0 linear, so this is emissive-only light (FX, windows, thrusters).
const BRIGHT = /* glsl */ `
precision highp float;
varying vec2 vUv;
uniform sampler2D tScene;
uniform vec2 uPx;
uniform float uThresh;
void main() {
  vec3 c = texture2D( tScene, vUv + uPx * vec2( -1.0, -1.0 ) ).rgb + texture2D( tScene, vUv + uPx * vec2( 1.0, -1.0 ) ).rgb
    + texture2D( tScene, vUv + uPx * vec2( -1.0, 1.0 ) ).rgb + texture2D( tScene, vUv + uPx * vec2( 1.0, 1.0 ) ).rgb;
  c *= 0.25;
  // NaN/Inf from any material would be smeared into big black squares by the bloom mips (Safari/Metal)
  if ( any( not( lessThan( abs( c ), vec3( 6.0e4 ) ) ) ) ) c = vec3( 0.0 );
  float l = max( c.r, max( c.g, c.b ) );
  // soft knee, capped so one hot pixel can't flood the frame
  float k = clamp( l - uThresh, 0.0, 4.0 );
  gl_FragColor = vec4( c * ( k / max( l, 1e-3 ) ), 1.0 );
}
`;

const FRAG = /* glsl */ `
precision highp float;
varying vec2 vUv;
uniform sampler2D tScene;
uniform sampler2D tBloom;   // quarter-res HDR overflow (emissive above the white point), mipmapped
uniform float uBloom;       // 0 disables the taps
uniform sampler2D tHud;
uniform float uHasHud;
uniform vec2 uRes;
uniform float uTime;
uniform float uExposure;
uniform float uGrain;
uniform float uVignette;
uniform float uFlash;      // detonation / hit flash, 0..1
uniform vec3 uFlashColor;
uniform float uHurt;       // red edge pulse 0..1
uniform float uChroma;     // world chromatic kick 0..1 (detonations)
uniform vec4 uShock[4];    // shooting feel: explosion shockwaves (uv centre xy, ring radius in screen heights, strength)
uniform float uDesat;      // downed / paused desaturation 0..1
uniform float uExtract;    // extraction grey wash 0..1 (the Godot Blood Moon): hot oranges and the train keep their colour
uniform vec3 uSunUv;       // xy: sun position in uv, z: visibility 0..1 (in front, near the frame)
uniform float uShafts;     // god-ray strength (0 disables the taps)
uniform float uWarmth;     // landscape grade strength
uniform float uTownMood;   // 0 meadow gold .. 1 town: neutral grade, no shafts
uniform float uNight;      // world/daynight.ts: 0 day .. 1 night (moonlight grade)
// visor
uniform float uVisorCurve;
uniform float uVisorSplit;
uniform float uVisorScan;
uniform float uVisorFlicker;
uniform sampler2D tHudGlass; // HUD lane: half-res glass mask (alpha = smoked-glass density)
uniform float uHasGlass;
uniform float uHudGlow;      // HUD lane: bloom of the projected light from the HUD's own mips
uniform float uRings[24];    // HUD lane: the LIFE / ORDNANCE gauge bands as SDF arcs (layout in hud.ts RING_*)

// ---- HUD lane: gauge bands drawn here so value changes cost no canvas work
const vec3 HR_PHOS = vec3( 0.894, 1.0, 0.957 );
const vec3 HR_SHIELD = vec3( 0.498, 0.839, 1.0 );
const vec3 HR_AMBER = vec3( 1.0, 0.69, 0.227 );
const vec3 HR_RED = vec3( 1.0, 0.231, 0.184 );
const vec3 HR_KEY = vec3( 0.008, 0.035, 0.04 );
void hrOver( inout vec4 o, vec3 c, float a ) { o = vec4( c * a, a ) + o * ( 1.0 - a ); }
// projected light has no dark key (James, 2026-10-21: no black borders; realism over readability)
void hrKey( inout vec4 o, float a ) {}
float hrBand( float d, float r, float w ) { return clamp( w * 0.5 - abs( d - r ) + 0.5, 0.0, 1.0 ); }
// coverage of [lo, hi] in gauge-fraction space, AA in arc-length px (L = px per unit fraction)
float hrSpan( float f, float lo, float hi, float L ) { return clamp( ( f - lo ) * L + 0.5, 0.0, 1.0 ) * clamp( ( hi - f ) * L + 0.5, 0.0, 1.0 ); }
float hrSeg( float f, float n, float L, float gp ) { float sl = L / n; float s = fract( f * n ) * sl; return clamp( min( s - gp, sl - gp - s ) + 0.5, 0.0, 1.0 ); }
// additive light the bands throw onto the glass (halo + glint); read after hudRings()
vec3 hrAdd;
float hrHalo( float d, float r, float w, float u ) { return exp( -max( 0.0, abs( d - r ) - w * 0.5 ) / ( 3.2 * u ) ); }
// a lit band: soft halo outside, the band, then a hotter core along its centre line
void hrLit( inout vec4 o, vec3 c, float a, float d, float r, float w, float span, float u, float al ) {
  hrAdd += c * 0.30 * a * hrHalo( d, r, w, u ) * span * al;
  hrOver( o, c, a * hrBand( d, r, w ) * span * al );
  hrOver( o, mix( c, vec3( 1.0 ), 0.65 ), 0.55 * a * hrBand( d, r, w * 0.34 ) * span * al );
}
// the curved glass catches the key light along its top-left edge
void hrGlint( vec2 q, float d, float u, float al ) {
  float k = pow( max( 0.0, dot( q / max( d, 1.0 ), vec2( -0.5, -0.866 ) ) ), 5.0 );
  hrAdd += vec3( 0.86, 0.97, 1.0 ) * k * al * ( 0.42 * hrBand( d, 112.5 * u, 1.3 * u ) + 0.07 * exp( -abs( d - 109.0 * u ) / ( 3.0 * u ) ) );
  // a faint specular sweep across the lens: a soft diagonal band of the key light, fading to the rim
  float sw = dot( q, vec2( 0.876, 0.482 ) ) + 34.0 * u;
  hrAdd += vec3( 0.86, 0.97, 1.0 ) * al * 0.055 * exp( -sw * sw / ( 260.0 * u * u ) ) * ( 1.0 - smoothstep( 96.0 * u, 114.0 * u, d ) );
}
float hrFrac( vec2 q, float mir ) {
  float a = atan( q.y, q.x );
  if ( mir > 0.5 ) a = 3.14159265 - a;
  float f = mod( a - 2.35619449, 6.28318531 ) / 4.71238898;
  return f > 1.16667 ? f - 1.33333 : f;
}
vec4 hudRings( vec2 p ) {
  vec4 o = vec4( 0.0 );
  hrAdd = vec3( 0.0 );
  float u = uRings[ 2 ], kp = max( 1.0, 1.15 * u ), hl = max( 1.0, floor( u + 0.5 ) );
  float blink = uRings[ 3 ];
  // LIFE: shield r95/5, hull r79/12 in 10 segments, heat band r64.5/3 + needle
  vec2 q = p - vec2( uRings[ 4 ], uRings[ 5 ] );
  float d = length( q );
  if ( d < 118.0 * u && d > 40.0 * u && uRings[ 14 ] > 0.0 ) {
    float f = hrFrac( q, 0.0 ), al = uRings[ 14 ];
    hrGlint( q, d, u, al );
    float sh = uRings[ 6 ], gs = uRings[ 7 ], hp = uRings[ 8 ], gh = uRings[ 9 ], ht = uRings[ 10 ];
    float hs = uRings[ 11 ], ss = uRings[ 12 ], hts = uRings[ 13 ];
    // shield
    float r = 95.0 * u, w = 3.0 * u, L = r * 4.71238898;
    if ( ss < 0.5 ) {
      hrKey( o, 0.62 * hrBand( d, r, w + 2.0 * kp ) * hrSpan( f, -kp / L, 1.0 + kp / L, L ) * al );
      float b = hrBand( d, r, w ) * al;
      hrOver( o, HR_SHIELD, 0.2 * b * hrSpan( f, 0.0, 1.0, L ) );
      hrOver( o, vec3( 1.0 ), 0.45 * b * hrSpan( f, sh, gs, L ) );
      hrLit( o, HR_SHIELD, 0.98, d, r, w, hrSpan( f, 0.0, sh, L ), u, al );
      if ( sh > 0.002 ) hrOver( o, HR_PHOS, hrBand( d, r, w * 1.8 ) * clamp( 0.8 * u - abs( f - sh ) * L + 0.5, 0.0, 1.0 ) * al );
    } else if ( ss < 1.5 ) {
      float dash = step( mod( f * L, 8.0 * u ), 3.0 * u );
      hrKey( o, 0.62 * hrBand( d, r, hl * 1.5 + 2.0 * kp ) * hrSpan( f, 0.0, 1.0, L ) * dash * al );
      hrOver( o, HR_RED, ( blink > 0.5 ? 0.6 : 0.25 ) * hrBand( d, r, hl * 1.5 ) * hrSpan( f, 0.0, 1.0, L ) * dash * al );
    }
    // shield break: the whole track flashes white and throws shards outward (uRings[15] = 1 -> 0)
    float fl = uRings[ 15 ];
    if ( fl > 0.0 && ss < 2.5 ) {
      float tr = hrSpan( f, 0.0, 1.0, L );
      hrOver( o, vec3( 1.0 ), fl * hrBand( d, r, w * 1.6 ) * tr * al );
      hrAdd += vec3( 0.75, 0.92, 1.0 ) * 0.6 * fl * hrHalo( d, r, w * 1.6, u ) * tr * al;
      float sr = r + ( 1.0 - fl ) * 16.0 * u + 4.0 * u;
      float sh2 = step( fract( f * 18.0 + 0.25 ), 0.38 ) * tr;
      hrKey( o, 0.5 * fl * hrBand( d, sr, 3.0 * u + 2.0 * kp ) * sh2 * al );
      hrOver( o, mix( HR_SHIELD, vec3( 1.0 ), 0.6 ), fl * hrBand( d, sr, 3.0 * u ) * sh2 * al );
    }
    // hull
    r = 79.0 * u; w = 7.0 * u; L = r * 4.71238898;
    float seg = hrSeg( f, 10.0, L, 1.6 * u ) * hrSpan( f, 0.0, 1.0, L );
    hrKey( o, 0.36 * hrBand( d, r, w + 2.0 * kp ) * seg * al );
    float b = hrBand( d, r, w ) * seg * al;
    hrOver( o, HR_PHOS, 0.14 * b );
    hrOver( o, hs > 0.5 ? HR_RED : vec3( 1.0 ), ( hs > 0.5 ? 0.6 : 0.42 ) * b * hrSpan( f, hp, gh, L ) );
    vec3 hc = hs > 2.5 ? HR_RED : hs > 1.5 ? HR_RED : hs > 0.5 ? HR_AMBER : HR_PHOS;
    float ha = hs > 2.5 ? 0.6 : 0.97;
    hrLit( o, hc, ha, d, r, w, seg * hrSpan( f, 0.0, hp, L ), u, al );
    // heat band
    r = 64.5 * u; w = 3.0 * u; L = r * 4.71238898;
    vec3 tc = hts > 3.5 ? HR_AMBER : hts > 2.5 ? HR_RED : hts > 1.5 ? HR_RED : hts > 0.5 ? HR_AMBER : HR_SHIELD;
    float ta = hts < 0.5 ? 0.6 : 0.98;
    float hspan = hrSpan( f, 0.0, ht, L );
    hrKey( o, 0.62 * hrBand( d, r, w + 2.0 * kp ) * hrSpan( f, -kp / L, ht + kp / L, L ) * step( 0.004, ht ) * al );
    hrOver( o, tc, ta * hrBand( d, r, w ) * hspan * al );
    // needle: capsule from r47 to r67 at the heat value
    float na = 2.35619449 + 4.71238898 * ht;
    vec2 nd = vec2( cos( na ), sin( na ) );
    float t = clamp( dot( q, nd ), 47.0 * u, 67.0 * u );
    float nd2 = length( q - nd * t );
    hrKey( o, 0.62 * clamp( 1.2 * u + kp - nd2 + 0.5, 0.0, 1.0 ) * al );
    hrOver( o, ht >= 0.8 ? HR_RED : HR_PHOS, clamp( 1.2 * u - nd2 + 0.5, 0.0, 1.0 ) * al );
  }
  // ORDNANCE (mirrored): ability r95/5, magazine r79/12
  q = p - vec2( uRings[ 16 ], uRings[ 17 ] );
  d = length( q );
  if ( d < 118.0 * u && d > 40.0 * u && uRings[ 23 ] > 0.0 ) {
    float f = hrFrac( q, 1.0 ), al = uRings[ 23 ];
    hrGlint( q, d, u, al );
    float mag = uRings[ 18 ], n = max( 1.0, uRings[ 19 ] ), rl = uRings[ 20 ], ab = uRings[ 21 ], st = uRings[ 22 ];
    float r = 95.0 * u, w = 3.0 * u, L = r * 4.71238898;
    if ( ab >= 0.0 ) {
      hrKey( o, 0.62 * hrBand( d, r, w + 2.0 * kp ) * hrSpan( f, -kp / L, 1.0 + kp / L, L ) * al );
      float b = hrBand( d, r, w ) * al;
      hrOver( o, HR_PHOS, 0.14 * b * hrSpan( f, 0.0, 1.0, L ) );
      hrLit( o, HR_AMBER, ab >= 1.0 ? 0.98 : 0.6, d, r, w, hrSpan( f, 0.0, ab, L ), u, al );
      if ( ab < 1.0 && ab > 0.002 ) hrOver( o, HR_PHOS, hrBand( d, r, w * 1.8 ) * clamp( 0.8 * u - abs( f - ab ) * L + 0.5, 0.0, 1.0 ) * al );
    } else hrOver( o, HR_PHOS, 0.14 * hrBand( d, r, hl ) * hrSpan( f, 0.0, 1.0, L ) * al );
    r = 79.0 * u; w = 7.0 * u; L = r * 4.71238898;
    bool reloading = rl >= 0.0;
    if ( st > 2.5 && !reloading ) {
      float dash = step( mod( f * L, 8.0 * u ), 4.0 * u );
      hrKey( o, 0.62 * hrBand( d, r, hl * 2.0 + 2.0 * kp ) * hrSpan( f, 0.0, 1.0, L ) * dash * al );
      hrOver( o, HR_RED, ( st > 3.5 ? 0.6 : 1.0 ) * hrBand( d, r, hl * 2.0 ) * hrSpan( f, 0.0, 1.0, L ) * dash * al );
    } else {
      float gp = ( n <= 12.0 ? 5.0 : n <= 24.0 ? 3.0 : 1.6 ) * u * 0.5;
      float seg = n <= 40.0 ? hrSeg( f, n, L, gp ) : 1.0;
      seg *= hrSpan( f, 0.0, 1.0, L );
      hrKey( o, 0.36 * hrBand( d, r, w + 2.0 * kp ) * seg * al );
      float b = hrBand( d, r, w ) * seg * al;
      hrOver( o, HR_PHOS, 0.14 * b );
      float shown = reloading ? ( n <= 40.0 ? floor( n * rl ) : n * rl ) : mag;
      vec3 lc = reloading ? HR_SHIELD : st > 0.5 ? HR_AMBER : HR_PHOS;
      float la = reloading ? 0.6 : st > 1.5 ? 0.6 : 0.97;
      hrLit( o, lc, la, d, r, w, seg * hrSpan( f, 0.0, shown / n, L ), u, al );
      if ( !reloading && mag > 0.0 && n <= 40.0 )
        hrOver( o, vec3( 1.0 ), hrBand( d, r - w * 0.5 + 1.5 * u, 3.0 * u ) * seg * hrSpan( f, ( mag - 1.0 ) / n, mag / n, L ) * al );
      if ( n > 40.0 ) {
        float per = 5.0 * L / n, s = f * L;
        float tk = clamp( 0.7 * u - abs( mod( s + per * 0.5, per ) - per * 0.5 ) + 0.5, 0.0, 1.0 ) * step( 0.5 * per, s ) * step( s, L - 0.5 * per );
        hrKey( o, 0.62 * tk * hrBand( d, r, w ) * al );
      }
      if ( reloading ) hrOver( o, HR_PHOS, hrBand( d, r, w * 1.8 ) * clamp( 0.8 * u - abs( f - rl ) * L + 0.5, 0.0, 1.0 ) * al );
    }
  }
  return o;
}

float hash( vec2 p ) { return fract( sin( dot( p, vec2( 127.1, 311.7 ) ) ) * 43758.5453 ); }

vec3 linearToSRGB( vec3 c ) {
  return mix( c * 12.92, 1.055 * pow( c, vec3( 1.0 / 2.4 ) ) - 0.055, step( 0.0031308, c ) );
}

// gentle filmic shoulder that keeps the printed flats flat
vec3 curve( vec3 x ) {
  x *= uExposure;
  // round 2: whites now land near 0.8 linear (was 0.63) so the golden haze glows instead of
  // reading as dull ochre; the toe is unchanged in shape
  return x * ( 1.0 + x / 4.0 ) / ( 1.0 + x * 0.55 );
}

// a refraction ring per blast: the scene bends outward across a moving band (HUD stays put)
vec2 shockOffset( vec2 uv ) {
  vec2 o = vec2( 0.0 );
  float asp = uRes.x / uRes.y;
  for ( int i = 0; i < 4; i++ ) {
    vec4 s = uShock[ i ];
    if ( s.w <= 0.0 ) continue;
    vec2 d = ( uv - s.xy ) * vec2( asp, 1.0 );
    float r = length( d );
    float w = 0.02 + s.z * 0.22;
    float x = ( r - s.z ) / w;
    float band = exp( -x * x ) * x; // pushes out on the leading edge, pulls on the trailing one
    o += d / max( r, 1e-4 ) * band * s.w * 0.02 * vec2( 1.0 / asp, 1.0 );
  }
  return o;
}

void main() {
  vec2 uv = vUv + shockOffset( vUv );
  vec2 px = 1.0 / uRes;
  // plate misregistration only on hits (uChroma); at rest it fringed every edge
  vec2 c = uv - 0.5;
  float kick = uChroma * 6.0;
  vec3 col;
  col.r = texture2D( tScene, uv + px * vec2( kick, 0.0 ) + c * uChroma * 0.012 ).r;
  vec4 sc0 = texture2D( tScene, uv );
  col.g = sc0.g;
  float trainKey = clamp( -sc0.a, 0.0, 1.0 ); // world/train.ts tags its hull with alpha -1
  col.b = texture2D( tScene, uv - px * vec2( kick, 0.0 ) - c * uChroma * 0.012 ).b;
  if ( any( not( lessThan( abs( col ), vec3( 6.0e4 ) ) ) ) ) col = vec3( 0.0 );

  // light shafts: march toward the sun and gather the bright haze/sky behind the silhouettes
  if ( uShafts > 0.0 && uSunUv.z > 0.0 && uTownMood < 0.99 ) {
    vec2 toSun = uSunUv.xy - uv;
    float sh = 0.0;
    float jit = hash( uv * uRes + fract( uTime ) * 13.0 );
    for ( int i = 0; i < 12; i++ ) {
      vec2 suv = uv + toSun * ( ( float( i ) + jit ) / 12.0 ) * 0.85;
      vec3 sc = texture2D( tScene, clamp( suv, 0.001, 0.999 ) ).rgb;
      sh += smoothstep( 0.78, 1.05, dot( sc, vec3( 0.3, 0.55, 0.15 ) ) );
    }
    sh /= 12.0;
    float fall = exp( -length( toSun * vec2( uRes.x / uRes.y, 1.0 ) ) * 1.6 );
    col += vec3( 1.0, 0.82, 0.5 ) * sh * fall * uShafts * uSunUv.z * ( 1.0 - 0.75 * uTownMood );
    // the sun's bloom in the haze
    col += vec3( 1.0, 0.78, 0.45 ) * pow( fall, 2.5 ) * 0.22 * uSunUv.z * ( 1.0 - 0.6 * uTownMood );
  }

  // bloom: only HDR overflow (emissive FX, windows, thrusters) gets here, so the meadow never blooms
  if ( uBloom > 0.0 ) {
    vec3 bl = textureLod( tBloom, uv, 0.5 ).rgb * 0.45 + textureLod( tBloom, uv, 2.0 ).rgb * 0.35 + textureLod( tBloom, uv, 3.5 ).rgb * 0.3;
    col += bl * uBloom;
  }

  // town: a touch more exposure so the street reads bright and clean (western_town.png)
  col *= 1.0 + 0.06 * uTownMood;
  col = curve( col );
  // palette pull: shadows lean teal, highlights lean gold (never grey)
  float l = dot( col, vec3( 0.299, 0.587, 0.114 ) );
  col = mix( col, col * vec3( 0.86, 1.02, 1.0 ) + vec3( 0.0, 0.004, 0.0035 ) * ( 1.0 - uTownMood ), smoothstep( 0.45, 0.0, l ) * 0.55 * ( 1.0 - 0.5 * uTownMood ) );
  col = mix( col, col * vec3( 1.04, 1.0, 0.9 ), smoothstep( 0.55, 1.0, l ) * 0.5 * ( 1.0 - 0.7 * uTownMood ) );
  // landscape grade (golden_grass_mechs.png): soft filmic toe that lifts the darks into a warm
  // teal-umber instead of black, gentle extra saturation in the golds, warm air over all
  {
    // round 3: the toe lift is nearly gone (it was a veil that cost the meadow its darks and
    // muddied the town's teal shade); a hair of teal stays in the meadow, none in town
    vec3 lift = vec3( 0.0015, 0.0025, 0.002 ) * ( 1.0 - uTownMood );
    vec3 g = col * ( 1.0 - lift ) + lift;
    // contrast about mid-grey (linear 0.18): a little in the meadow, more in town
    g = 0.18 * pow( max( g, vec3( 0.0 ) ) / 0.18, vec3( 1.06 + 0.1 * uTownMood ) );
    float gl = dot( g, vec3( 0.299, 0.587, 0.114 ) );
    g = mix( vec3( gl ), g, 1.12 );
    g *= mix( vec3( 1.03, 1.0, 0.93 ), vec3( 1.0 ), uTownMood );
    col = mix( col, g, uWarmth );
    l = dot( col, vec3( 0.299, 0.587, 0.114 ) );
  }
  // night: moonlight vision. Mid and dark values lose their warmth and lean blue (the eye's
  // night shift); the bright lights (windows, thrusters, fireflies, FX) keep their colour
  if ( uNight > 0.0 ) {
    float nl = dot( col, vec3( 0.299, 0.587, 0.114 ) );
    // a printed night, not a grey one: split-tone that KEEPS saturation, deep indigo in the
    // darks, teal-blue in the mids, warm lights left alone
    vec3 moon = col * mix( vec3( 0.66, 0.76, 1.08 ), vec3( 0.78, 0.94, 0.96 ), smoothstep( 0.03, 0.25, nl ) );
    moon = mix( vec3( dot( moon, vec3( 0.299, 0.587, 0.114 ) ) ), moon, 0.68 ); // slate-teal, not royal blue
    moon += vec3( 0.004, 0.003, 0.012 ) * ( 1.0 - smoothstep( 0.0, 0.08, nl ) );
    col = mix( col, moon, uNight * ( 1.0 - smoothstep( 0.45, 0.95, nl ) ) );
    l = dot( col, vec3( 0.299, 0.587, 0.114 ) );
  }
  // extraction: the world drains to a cold grey and a harder contrast, except saturated hot
  // oranges and golds (the bell, its beam, fire, thrusters), which read like a colour key
  if ( uExtract > 0.0 ) {
    float mx = max( col.r, max( col.g, col.b ) ), mn = min( col.r, min( col.g, col.b ) );
    float sat = ( mx - mn ) / max( mx, 1e-4 );
    float warm = smoothstep( 0.12, 0.32, col.r - max( col.b, col.g * 0.62 ) ) * step( col.b, col.g );
    float keep = smoothstep( 0.62, 0.86, sat ) * warm * smoothstep( 0.42, 0.8, mx );
    vec3 grey = vec3( l ) * vec3( 0.97, 0.99, 1.03 );
    grey = 0.18 * pow( max( grey, vec3( 0.0 ) ) / 0.18, vec3( 1.12 ) );
    // the space train is the way out, so it keeps its own livery (no warm shift) like the bell keeps its gold
    col = mix( col, grey, uExtract * 0.82 * ( 1.0 - max( keep, trainKey ) ) );
    col = mix( col, col * vec3( 1.08, 0.92, 0.84 ), uExtract * keep * 0.4 );
    l = dot( col, vec3( 0.299, 0.587, 0.114 ) );
  }
  // desaturate when downed or paused
  col = mix( col, vec3( l ) * vec3( 1.02, 0.98, 0.9 ), uDesat * 0.75 );

  col = linearToSRGB( clamp( col, 0.0, 1.0 ) );

  // paper grain + fibre
  float g = hash( floor( uv * uRes ) + fract( uTime * 7.0 ) * 91.0 ) - 0.5;
  float fibre = hash( floor( uv * uRes * vec2( 0.25, 1.0 ) ) ) - 0.5;
  col += ( g * 0.75 + fibre * 0.25 ) * uGrain * ( 1.0 - 0.15 * uTownMood );
  // vignette
  float v = smoothstep( 0.95, 0.25, length( c * vec2( 1.0, 0.8 ) ) );
  col *= mix( vec3( 1.0 - uVignette * ( 1.0 - 0.5 * uTownMood ) ) * mix( vec3( 1.0, 0.93, 0.82 ), vec3( 1.0 ), uTownMood ), vec3( 1.0 ), v );
  // hurt: red at the edges (gameplay signal colour)
  float edge = smoothstep( 0.35, 0.75, length( c * vec2( 1.1, 1.0 ) ) );
  col = mix( col, vec3( 0.9, 0.13, 0.08 ), edge * uHurt * 0.55 );
  // flash
  col = mix( col, uFlashColor, uFlash * 0.35 );

  // ------------------------------------------------------------ visor HUD
  if ( uHasHud > 0.5 ) {
    vec2 hc = vUv - 0.5;
    vec2 ha = hc * vec2( uRes.x / uRes.y, 1.0 );
    float r2 = dot( ha, ha );
    // barrel curvature of the visor
    vec2 uvG = 0.5 + hc * ( 1.0 + uVisorCurve * r2 );
    // lateral chromatic split: nil at the boresight, uVisorSplit px at the corners
    vec2 sp = 2.0 * hc * r2 * uVisorSplit * px;
    vec4 hg = textureLod( tHud, uvG, 0.0 );
    sp *= 1.0 - 0.6 * smoothstep( 0.35, 0.9, hg.a ); // text and plates stay registered; the split lives at edges
    vec4 hr = textureLod( tHud, uvG + sp, 0.0 );
    vec4 hb = textureLod( tHud, uvG - sp, 0.0 );
    vec4 h = vec4( hr.r, hg.g, hb.b, max( hg.a, max( hr.a, hb.a ) ) );
    // nothing outside the glass
    vec2 inG = step( vec2( 0.0 ), uvG ) * step( uvG, vec2( 1.0 ) );
    float inside = inG.x * inG.y;
    h *= inside;
    // smoked glass under the instruments: frosted, desaturated, cooled, and darkened MORE over
    // bright haze (adaptive density) so the phosphor always has a ground
    if ( uHasGlass > 0.5 && false ) { // retired: a projection has no glass panel behind it
      float gm = texture2D( tHudGlass, uvG ).a * inside;
      if ( gm > 0.003 ) {
        vec2 o1 = px * 2.5, o2 = px * 5.5;
        vec3 bl = texture2D( tScene, uv ).rgb * 0.2
          + ( texture2D( tScene, uv + vec2( o1.x, 0.0 ) ).rgb + texture2D( tScene, uv - vec2( o1.x, 0.0 ) ).rgb
            + texture2D( tScene, uv + vec2( 0.0, o1.y ) ).rgb + texture2D( tScene, uv - vec2( 0.0, o1.y ) ).rgb ) * 0.12
          + ( texture2D( tScene, uv + o2 ).rgb + texture2D( tScene, uv - o2 ).rgb
            + texture2D( tScene, uv + vec2( o2.x, -o2.y ) ).rgb + texture2D( tScene, uv + vec2( -o2.x, o2.y ) ).rgb ) * 0.08;
        vec3 b = linearToSRGB( clamp( curve( bl ), 0.0, 1.0 ) );
        float lb = dot( b, vec3( 0.299, 0.587, 0.114 ) );
        vec3 smoke = mix( vec3( lb ), b, 0.72 ) * vec3( 0.88, 0.96, 1.0 );
        // the world stays visible: half the graded pixel survives under the frost, tinted by density
        float dens = mix( 0.24, 0.50, smoothstep( 0.2, 0.8, lb ) );
        col = mix( col, mix( col, smoke, 0.5 ) * ( 1.0 - dens ) + vec3( 0.004, 0.012, 0.014 ), gm );
      }
    }
    // the gauge bands (SDF, from uniforms), under the canvas ink
    if ( uRings[ 0 ] > 0.0 ) {
      vec4 rg = hudRings( vec2( uvG.x * uRings[ 0 ], ( 1.0 - uvG.y ) * uRings[ 1 ] ) ) * inside;
      col = col * ( 1.0 - 0.22 * rg.a ) + rg.rgb * 0.82 * ( 1.0 + uHudGlow * 0.25 ) + hrAdd * inside;
    }
    // projected light: faint scan, slow flicker, and a bloom read from the HUD's own mips
    float scan = 1.0 - uVisorScan * ( 0.5 + 0.5 * sin( uv.y * uRes.y * 1.6 + uTime * 3.0 ) );
    float flick = 1.0 - uVisorFlicker * ( 0.5 + 0.5 * sin( uTime * 37.0 ) * sin( uTime * 11.0 ) );
    vec3 bloom = ( textureLod( tHud, uvG, 2.0 ).rgb * 0.55 + textureLod( tHud, uvG, 3.6 ).rgb * 0.45 ) * inside;
    // projected composite: the light mostly ADDS to the world (a faint dimming under it keeps the
    // strokes from vanishing on white, but there is no dark key), then the bloom
    col = col * ( 1.0 - 0.22 * h.a ) + h.rgb * 0.86 * scan * flick + bloom * uHudGlow * flick;
  }
  gl_FragColor = vec4( col, 1.0 );
}
`;

export class PostPipeline {
  readonly target: THREE.WebGLRenderTarget;
  readonly material: THREE.ShaderMaterial;
  private quad: THREE.Mesh;
  private scene = new THREE.Scene();
  private cam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  private renderer: THREE.WebGLRenderer;
  scale = 1;
  /** HDR bloom strength; 0 skips the bright pass entirely (low tier) */
  bloom = 0;
  private bloomRT: THREE.WebGLRenderTarget;
  private brightMat: THREE.ShaderMaterial;
  private brightQuad: THREE.Mesh;
  private brightScene = new THREE.Scene();

  constructor(renderer: THREE.WebGLRenderer, samples = 4) {
    this.renderer = renderer;
    const size = renderer.getDrawingBufferSize(new THREE.Vector2());
    this.target = new THREE.WebGLRenderTarget(size.x, size.y, {
      type: THREE.HalfFloatType, samples, depthBuffer: true,
      minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter,
    });
    this.material = new THREE.ShaderMaterial({
      vertexShader: VERT, fragmentShader: FRAG, depthTest: false, depthWrite: false,
      uniforms: {
        tScene: { value: this.target.texture }, tHud: { value: null }, uHasHud: { value: 0 },
        uRes: { value: new THREE.Vector2(size.x, size.y) }, uTime: { value: 0 },
        uExposure: { value: 1.0 }, uGrain: { value: 0.045 }, uVignette: { value: 0.2 },
        uFlash: { value: 0 }, uFlashColor: { value: new THREE.Color(0xfff2d0) }, uHurt: { value: 0 },
        uChroma: { value: 0 }, uDesat: { value: 0 }, uExtract: { value: 0 },
        uShock: { value: [new THREE.Vector4(), new THREE.Vector4(), new THREE.Vector4(), new THREE.Vector4()] },
        uSunUv: { value: new THREE.Vector3(0.5, 0.5, 0) }, uShafts: { value: samples > 0 ? 0.32 : 0 }, uWarmth: { value: 1 }, uTownMood: HAZE.uTownMood, uNight: HAZE.uNight,
        uVisorCurve: { value: 0.075 }, uVisorSplit: { value: 1.3 }, uVisorScan: { value: 0.04 }, uVisorFlicker: { value: 0.02 },
        tHudGlass: { value: null }, uHasGlass: { value: 0 }, uHudGlow: { value: 0.55 },
        uRings: { value: new Float32Array(24) },
        tBloom: { value: null }, uBloom: { value: 0 },
      },
    });
    this.bloomRT = new THREE.WebGLRenderTarget(Math.max(1, size.x >> 2), Math.max(1, size.y >> 2), {
      type: THREE.HalfFloatType, depthBuffer: false, generateMipmaps: true,
      minFilter: THREE.LinearMipmapLinearFilter, magFilter: THREE.LinearFilter,
    });
    this.brightMat = new THREE.ShaderMaterial({
      vertexShader: VERT, fragmentShader: BRIGHT, depthTest: false, depthWrite: false,
      uniforms: { tScene: { value: this.target.texture }, uPx: { value: new THREE.Vector2(1 / size.x, 1 / size.y) }, uThresh: { value: 1.25 } },
    });
    this.material.uniforms.tBloom.value = this.bloomRT.texture;
    this.bloom = samples > 0 ? 0.8 : 0;
    this.brightQuad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this.brightMat);
    this.brightQuad.frustumCulled = false;
    this.brightScene.add(this.brightQuad);
    this.quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this.material);
    this.quad.frustumCulled = false;
    this.scene.add(this.quad);
  }

  setSize(w: number, h: number) {
    const tw = Math.max(1, Math.round(w * this.scale)), th = Math.max(1, Math.round(h * this.scale));
    this.target.setSize(tw, th);
    this.bloomRT.setSize(Math.max(1, tw >> 2), Math.max(1, th >> 2));
    (this.brightMat.uniforms.uPx.value as THREE.Vector2).set(1 / tw, 1 / th);
    (this.material.uniforms.uRes.value as THREE.Vector2).set(w, h);
  }

  setHud(tex: THREE.Texture | null) {
    this.material.uniforms.tHud.value = tex;
    this.material.uniforms.uHasHud.value = tex ? 1 : 0;
    // HUD lane: the smoked-glass mask rides on the HUD texture (hud.ts sets userData.glass)
    const glass = (tex?.userData?.glass as THREE.Texture | undefined) ?? null;
    this.material.uniforms.tHudGlass.value = glass;
    this.material.uniforms.uHasGlass.value = glass ? 1 : 0;
    // HUD lane: the gauge-band uniforms are the HUD's own Float32Array (hud.ts writes, three uploads)
    const rings = tex?.userData?.rings as Float32Array | undefined;
    this.material.uniforms.uRings.value = rings ?? new Float32Array(24);
  }

  /** sun direction for the shafts (shared with HAZE.uSunDir); set once */
  sunDir: THREE.Vector3 | null = null;
  private sunP = new THREE.Vector3();
  private camDir = new THREE.Vector3();

  render(scene: THREE.Scene, camera: THREE.Camera, time: number) {
    const r = this.renderer;
    this.material.uniforms.uTime.value = time;
    const su = this.material.uniforms.uSunUv.value as THREE.Vector3;
    if (this.sunDir) {
      camera.getWorldDirection(this.camDir);
      const facing = this.camDir.dot(this.sunDir);
      this.sunP.copy(camera.position).addScaledVector(this.sunDir, 500).project(camera);
      const off = Math.max(Math.abs(this.sunP.x), Math.abs(this.sunP.y));
      su.set(this.sunP.x * 0.5 + 0.5, this.sunP.y * 0.5 + 0.5, facing > 0 ? Math.max(0, Math.min(1, (1.9 - off) / 0.9)) * Math.min(1, facing * 3) : 0);
    } else su.z = 0;
    r.setRenderTarget(this.target);
    r.render(scene, camera);
    this.material.uniforms.uBloom.value = this.bloom;
    if (this.bloom > 0) {
      r.setRenderTarget(this.bloomRT);
      r.render(this.brightScene, this.cam);
    }
    r.setRenderTarget(null);
    r.render(this.scene, this.cam);
  }
}
